import React, { useEffect } from "react";
import "~/css/DictionaryForm.css";
import { useDictionary } from "./HamburgerMenu/hooks/useDictionary";
import { useTranslation } from "react-i18next";
import { DictionaryObject } from "~/TTS/voicevox/VoicevoxUtils";

export interface DictionaryFormProps {
  onSaved?: () => void;
  onCancel?: () => void;
  registeredDicts?: DictionaryObject[];
}

/**
 * 辞書の新規登録
 */
const DictionaryForm: React.FC<DictionaryFormProps> = ({ onSaved, onCancel, registeredDicts }) => {
  const { t } = useTranslation();
  const form = useDictionary({ mode: "create", onSaved, onCancel, registeredDicts });
  const {
    word,
    kana,
    priority,
    accent,
    moras,
    isProcessing,
    setWord,
    setKana,
    setPriority,
    setAccent,
    preview,
    save,
    cancel,
    suggestKana,
    markKanaEdited,
  } = form;

  // 言葉入力欄のフォーカスが外れたときに読み（カナ）を自動生成
  const handleWordBlur = async () => {
    await suggestKana();
  };

  // 読み（カナ）が変更されたら、変更済みフラグを設定
  const handleKanaChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    markKanaEdited();
    setKana(e.target.value.replace(/\s/g, ""));
  };

  useEffect(() => {
    /* hook内部でリセット済み */
  }, [kana]);

  return (
    <div className="dict-form-wrapper">
      <div className="dict-form-body">
        <div className="form-row">
          <div className="field-inline">
            <span className="field-label">{t("dictionaryWord")}</span>
            <input
              type="text"
              name="word"
              className="form-input"
              placeholder={t("example", { attribute: t("exampleDictionaryWord") })}
              value={word}
              onChange={(e) => setWord(e.target.value.replace(/\s/g, ""))}
              onBlur={handleWordBlur}
            />
          </div>
        </div>
        <div className="form-row">
          <div className="field-inline">
            <span className="field-label">{t("pronunciation")}</span>
            <input
              type="text"
              name="wordKana"
              className="form-input"
              placeholder={t("example", { attribute: "ヒマラヤサンミャク" })}
              value={kana}
              onChange={handleKanaChange}
            />
          </div>
        </div>
        <div className="form-row">
          <div className="field-inline">
            <span className="field-label">{t("pronunciationPriority")}</span>
            <div className="priority-row">
              <span className="priority-edge">{t("low")}</span>
              <div className="priority-slider-composite">
                <div className="ps-track">
                  <div
                    className="ps-track-active"
                    style={{ width: `${((priority - 1) / 9) * 100}%` }}
                  />
                  <div className="ps-ticks" aria-hidden>
                    {Array.from({ length: 10 }, (_, i) => {
                      const pos = i + 1;
                      const active = pos <= priority;
                      const current = pos === priority;
                      return (
                        <span
                          key={pos}
                          className={`ps-tick ${active ? "active" : ""} ${current ? "current" : ""}`}
                        />
                      );
                    })}
                  </div>
                </div>
                <input
                  type="range"
                  min={1}
                  max={10}
                  value={priority}
                  onChange={(e) => setPriority(Number(e.target.value))}
                  className="ps-range-overlay"
                  aria-label={`${t("priority")}`}
                />
              </div>
              <span className="priority-edge">{t("high")}</span>
              <span className="priority-value">{priority}</span>
            </div>
          </div>
        </div>
        <div className="form-row">
          <div className="field-inline">
            <span className="field-label">{t("accent")}</span>
            <div className="accent-grid" style={{ flex: 1 }}>
              {moras.map((m, i) => {
                const idx = i + 1;
                return (
                  <div
                    key={i}
                    className={`accent-cell ${accent === idx ? "selected" : ""}`}
                    onClick={() => setAccent(idx)}
                  >
                    <span className="mora">{m}</span>
                    <input
                      type="radio"
                      name="accent"
                      checked={accent === idx}
                      onChange={() => setAccent(idx)}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="form-actions">
          <button type="button" className="btn-cancel" onClick={cancel} disabled={isProcessing}>
            {t("cancel")}
          </button>
          <button type="button" className="btn-preview" onClick={preview} disabled={isProcessing}>
            {t("preview")}
          </button>
          <button type="button" className="btn-regist" onClick={save} disabled={isProcessing}>
            {t("newRegistration")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DictionaryForm;
